import { InvitationsTable } from '@/components/invitations';
import { PageHeader, PageSurface } from '@/components/ui';
import { LoadingState } from '@/components/ui/loading-state';
import { fetchProgramById, useProgramQuery } from '@/hooks/queries/use-affiliate-program-query';
import { ensureValidToken } from '@/libs/auth-guard';
import useNavBarStore from '@/stores/nav-store';
import { Container } from '@chakra-ui/react';
import type { Program } from '@fluenceur/types';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoaderFunctionArgs } from 'react-router-dom';
import { useLoaderData } from 'react-router-dom';

interface ProgramInvitationsLoaderData {
  program: Program;
}

const ProgramInvitationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { program: programFromLoader } = useLoaderData() as ProgramInvitationsLoaderData;

  const { data: program, isLoading: isProgramLoading } = useProgramQuery({
    programId: programFromLoader.id,
    initialData: programFromLoader,
  });

  const { setData } = useNavBarStore();

  useEffect(() => {
    // Seed the program panel's title. The panel is opened from the route.
    const currentProgram = program || programFromLoader;
    if (currentProgram) {
      setData({ id: currentProgram.id, name: currentProgram.name });
    }
  }, [program, programFromLoader, setData]);

  // Determine the program to display. Prioritize hook's data.
  const displayProgram = program || programFromLoader;

  if (isProgramLoading && !displayProgram) {
    return <LoadingState message={t('programs.programInvitations.loadingProgram')} />;
  }

  if (!displayProgram) {
    return <p>{t('programs.programInvitations.programNotFound')}</p>;
  }

  return (
    <PageSurface surfaceProps={{ overflow: 'auto' }}>
      <Container maxW="full" py={{ base: 5, xl: 6 }} px={{ base: 4, xl: 6 }}>
        <PageHeader
          title={t('programs.programInvitations.title')}
          description={t('programs.programInvitations.description')}
        />
        {/* Standalone Invitation Table with built-in filters and data fetching */}
        <InvitationsTable programId={displayProgram.id} />
      </Container>
    </PageSurface>
  );
};

export const programInvitationsLoader = async ({ params }: LoaderFunctionArgs) => {
  await ensureValidToken();

  const { id } = params;
  if (!id) throw new Response('Not Found', { status: 404 });

  const programData = await fetchProgramById(id);

  if (!programData) {
    throw new Response('Program Not Found', { status: 404 });
  }

  return { program: programData };
};

export default ProgramInvitationsPage;
